import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import queryString from 'query-string';

import { Table, TableBody, TableHead, TableCell, TableContainer, TableRow } from '@mui/material';
import { Row, Col, NavLink } from 'react-bootstrap';
import Chip from '@mui/material/Chip';

import { getDashboardStatsEquipmentCount, getDashboardStatsEquipmentStatus } from 'components/axios/dashboardAxios';
import { getEquipmentZoneByCode } from 'components/axios/equipmentZoneAxios';

import { useEnumApi } from 'contexts/EnumApiContext';
import MainCard from 'components/MainCard';
import { Link } from 'react-router-dom';
import { equipmentISOStatus, getEquipmentCtgrImageNameByCodeOfDashboardSubNantong, getEquipmentStatusColorByStatus, getTemperatureColor, ROLE } from 'data/data';
import { getEquipmentAlertsByQueryString } from 'components/axios/equipmentAlertsAxios';
import AlertListCard from 'components/inner/mainCard/AlertListCard';
import { useQuery } from '@tanstack/react-query';

const DELAY_TIME = Number(process.env.REACT_APP_DELAY_TIME) || 60000;

function DashboardNantongMoniteringSub() {
  const isViewCommError = process.env.REACT_APP_SHOW_NETWORK_ERROR_TO_ALL_YN === 'Y';
  const { getEnumValueByEnumName, getEnumValueByEnumNameAndCode, equipmentCategoryLevel1s, useNantongSubwayLines, role } = useEnumApi();
  const { subwayLineByCode } = useNantongSubwayLines();
  const { t } = useTranslation();

  const COMM_STATUS_LIST = [
    { id: 'N', value: false,  name: t('message.common.normal') },
    { id: 'Y', value: true,   name: t('message.common.subtitle.communication.issue') },
  ];

  const COMM_STATUS_MAP = COMM_STATUS_LIST.reduce((acc, item) => {
    acc[item.value] = item.name;
    return acc;
  }, {});

  let qs = queryString.parse(window.location.search);
  
  const EquipmentFaultStatusList =
    useMemo(() => {
      return (
        getEnumValueByEnumName( "EquipmentFaultStatus" )?.map((efs) => ({
          ...efs,
          code2:
            `count${efs.code.replace('_', '')}`
        })) ?? []
      );
    }, [getEnumValueByEnumName]);

  // =========================
  // query
  // =========================
  const {
    data,
    isLoading,
    isFetching,
    isSuccess
  } = useQuery({
    queryKey: [
      'nantong-station-dashboard',
      qs.stationId
    ],

    enabled:
      !!qs.stationId &&
      equipmentCategoryLevel1s?.length > 0,

    refetchInterval: DELAY_TIME,

    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // 🔥 탭 안 보여도 멈추지 않음(기본값은 false)
    refetchIntervalInBackground: true,

    staleTime: DELAY_TIME,

    placeholderData: (prev) => prev,

    queryFn: async () => {
      let equipmentParam = {
        iotInstlYn: true,
        zoneCode: qs.stationId,
        opStatus : "O"
      };

      let alertParam = {
        page: 0,
        size: 5,
        "equipment.iotInstlYn": true,
        "equipment.opStatus": "O",
        "equipment.zone.code": qs.stationId
      };

      let addQueryString =
        "&sort=status,asc&sort=alertedAt,desc";

      let queryStringValue = Object.entries(alertParam).map(([key, value]) => {
          if ( value === undefined || value === null ) {
            return null;
          }
          return `${key}=${value}`;
        })
        .filter(Boolean)
        .join('&');
      queryStringValue += addQueryString;

      const [
        stationData,
        equipmentCounts,
        equipmentDatas,
        equipmentAlerts,
      ] = await Promise.all([
        getEquipmentZoneByCode( qs.stationId ),
        getDashboardStatsEquipmentCount( equipmentParam ),
        getDashboardStatsEquipmentStatus( equipmentParam ),
        getEquipmentAlertsByQueryString( "?" + queryStringValue ),
      ]);

      return {
        stationData,
        equipmentCounts,
        equipmentDatas,
        equipmentAlerts
      };
    }
  });

  // =========================
  // raw data
  // =========================
  const station         = data?.stationData;
  const equipmentCounts = data?.equipmentCounts;
  const equipmentDatas  = data?.equipmentDatas;
  const alertDatas      = data?.equipmentAlerts;

  // =========================
  // equipment count
  // =========================
  const equipmentIotCount =
    useMemo(() => {
      return (
        equipmentCounts?.equipmentCount?.total ?? 0
      );
    }, [equipmentCounts]);

  // =========================
  // status data
  // =========================
  const statusDatas =
    useMemo(() => {
      return (
        EquipmentFaultStatusList?.map(element => ({
            name: element.code,
            count:
              equipmentCounts?.equipmentCount?.[element.code2] ?? 0
          })) ?? []
      );
    }, [
      EquipmentFaultStatusList,
      equipmentCounts
    ]);

  // =========================
  // equipments
  // =========================
  const equipments =
    useMemo(() => {
      return (
        equipmentCategoryLevel1s?.map((lvl1) => {
            const equipmentTopCategories = equipmentDatas?.equipmentTopCategories?.find( etc => etc.code === lvl1.code );
            return {
              ...lvl1,
              equipmentStatuses: equipmentTopCategories?.equipmentStatuses
            };
          }) ?? []
      );
    }, [
      equipmentCategoryLevel1s,
      equipmentDatas
    ]);

  // =========================
  // equipment category result
  // =========================
  const equipmentCtgrResult =
    useMemo(() => {
      const equipmentTopCategories = equipmentCounts?.equipmentTopCategories;

      return (
        equipmentCategoryLevel1s?.map((lvl1) => {
            const topCategory = equipmentTopCategories?.find( etc => etc.code === lvl1.code );
            return {
              code: lvl1.code,
              data:
                EquipmentFaultStatusList?.map(element => ({
                    name: element.code,
                    count: topCategory?.equipmentCount?.[ element.code2 ] ?? 0
                  }))
            };
          }) ?? []
      );

    }, [
      equipmentCounts,
      equipmentCategoryLevel1s,
      EquipmentFaultStatusList
    ]);

  // =========================
  // select tab
  // =========================
  const [selectTab, setSelectTab] = useState('ALL');

  // =========================
  // summary count
  // =========================
  const normalCnt =
    useMemo(() => {
      return (
        statusDatas?.reduce(
          (acc, cur) => {
            if (cur.name === 'O1_N') {
              return acc + cur.count;
            }
            return acc;
          }, 0 ) ?? 0
      );

    }, [statusDatas]);

  const communiCnt =
    useMemo(() => {
      return (
        statusDatas?.reduce(
          (acc, cur) => {
            if (cur.name === 'O4_C') {
              return acc + cur.count;
            }
            return acc;
          }, 0 ) ?? 0
      );

    }, [statusDatas]);

  const alertCnt =
    useMemo(() => {
      return (
        statusDatas?.reduce(
          (acc, cur) => {
            if (cur.name === 'O3_A') {
              return acc + cur.count;
            }
            return acc;
          }, 0 ) ?? 0
      );

    }, [statusDatas]);

  const referCnt =
    useMemo(() => {
      return (
        statusDatas?.reduce(
          (acc, cur) => {
            if (cur.name === 'O2_I') {
              return acc + cur.count;
            }
            return acc;
          }, 0 ) ?? 0
      );

    }, [statusDatas]);

  // =========================
  // css
  // =========================
  const {
    stationPumpCss,
    stationEsCss,
    stationAirCss
  } = useMemo(() => {
    let station_pump_css = 'sub_station_left1';
    let station_es_css = 'sub_station_right';
    let station_air_css = 'sub_station_left2';

    equipmentCategoryLevel1s?.forEach(
      (lvl1) => {
        const categoryData = equipmentCtgrResult?.find( ctgr => ctgr.code === lvl1.code );

        const alertCtrlCnt = categoryData?.data?.reduce(
            (acc, cur) => {
              if (
                cur.name === 'O3_A'
              ) {
                return acc + cur.count;
              }
              return acc;
            }, 0 );

        const referCtrlCnt = categoryData?.data?.reduce(
            (acc, cur) => {
              if (
                cur.name === 'O2_I'
              ) {
                return acc + cur.count;
              }
              return acc;
            }, 0 );

        if (alertCtrlCnt > 0) {
          if (lvl1.code === 'PUMP') {
            station_pump_css = "sub_station_pumpJ";
          }
          if (lvl1.code === 'ES') {
            station_es_css = "sub_station_esJ";
          }
          if (lvl1.code === 'AHU') {
            station_air_css = "sub_station_airJ";
          }
        } else {
          if (referCtrlCnt > 0) {
            if (lvl1.code === 'PUMP') {
              station_pump_css = "sub_station_pumpY";
            }
            if (lvl1.code === 'ES') {
              station_es_css = "sub_station_esY";
            }
            if (lvl1.code === 'AHU') {
              station_air_css = "sub_station_airY";
            }
          }
        }
      }
    );

    return {
      stationPumpCss: station_pump_css,
      stationEsCss: station_es_css,
      stationAirCss: station_air_css
    };

  }, [
    equipmentCategoryLevel1s,
    equipmentCtgrResult
  ]);

  // =========================
  // loading
  // =========================
  const isDataLoaded =
    isSuccess && !!station;

  if (!isDataLoaded) {
    return null;
  }

  let main_container_css = 'main_container1_nt';

  return (
      <div className={`main_container ${main_container_css} cn_main_container`} style={{padding: "10px"}}>
        {/* 네비게이션 */}
        <div className="sub_subwayline">
          <span>･</span>{subwayLineByCode(station.category.code).title} <span className="gt">&gt;</span> {station.name}
        </div>

        <Row className="g-3 nt-monitoring-top" style={{justifyContent: "space-between"}}>
          <Col sm={8} className="main_right" style={{position: "relative"}}>
            {/* train station */}
            <div className="sub_motion_train" style={{left: "20px", top: "0px"}}>
              {/* sub_station_left1, sub_station_pumpR, sub_station_pumpJ */}
              {/* {station_pump_css === 'sub_station_left1' ? */}
              <div className={stationPumpCss}></div>
              {/* :
              <Link onClick={()=>{setSelectTab('PUMP')}}><div className={stationPumpCss}></div></Link>
              } */}

              {/* sub_station_left2, sub_station_airR, sub_station_airJ */}
              {/* {station_air_css === 'sub_station_left2' ? */}
              <div className={stationAirCss}></div>
              {/* :
              <Link onClick={()=>{setSelectTab('AHU')}}><div className={stationAirCss}></div></Link>
              } */}

              {/* sub_station_right, sub_station_esR, sub_station_esJ */}
              {/* {station_es_css === 'sub_station_right' ? */}
              <div className={stationEsCss}></div>
              {/* :
              <Link onClick={()=>{setSelectTab('ES')}}><div className={stationEsCss}></div></Link>
              } */}

              <div className="sub_station_up"></div>
              <div className="sub_station_down"></div>
              {/* sub_elevator_down sub_elevator_down_stop sub_elevator_up sub_elevator_up_stop sub_elevator_default */}
              <div className="sub_elevator_default"></div>
              <div className="sub_railload"></div>

              {/* sub_inner_train_in, sub_inner_train_out, sub_inner_train_default */}
              <div className="sub_inner_train_in">
                <img src={`${process.env.PUBLIC_URL}/img/sub_inner_train_default${station.category?.code==='NT_L2' ? '2':''}.gif`} alt='inner_train' />
              </div>

              {/* sub_outside_train_in, sub_outside_train_out, sub_outside_train_default */}
              <div className="sub_outside_train_out">
                <img src={`${process.env.PUBLIC_URL}/img/sub_outside_train_default${station.category?.code==='NT_L2' ? '2':''}.gif`} alt='outside_train' />
              </div>
            </div>
          </Col>

          <Col sm={4} className="main_left sub_left" style={{marginTop: "10px"}}>
            {/* registration status */}
            <div className='cn_sub_reg graCard' style={{marginBottom: "10px"}}>
              <MainCard  content={false} title="">
                <div div style={{display: "flex", alignItems: "center"}}>
                  <div style={{padding:"5px 10px"}}>
                    <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/dashboard.png`} alt='' /></span>&nbsp;
                    <Chip label={t('message.common.chip.registration.status')} color="default" />
                  </div>
                </div>

                <TableContainer className="table_part">
                  <Table aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('message.common.subtitle.managerName')}</TableCell>
                        <TableCell>{t('message.common.subtitle.tel.number')}</TableCell>
                        <TableCell>{t('message.common.subtitle.equipment.count')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{station && station.contactName}</TableCell>
                        <TableCell>{station && station.contactNumber}</TableCell>
                        <TableCell>{equipmentIotCount}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>                          
              </MainCard>
            </div>  

            {/* status count */}
            <div className='cn_sub_status graCard' style={{marginBottom: "10px"}}>
              <MainCard  content={false} title="">
                <div div style={{display: "flex", alignItems: "center"}}>
                  <div style={{padding:"5px 10px"}}>
                    <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/dashboard.png`} alt='' /></span>&nbsp;
                    <Chip label={t('message.common.chip.situation.status')} color="default" />
                  </div>
                </div>
                
                <TableContainer className="table_part">
                  <Table aria-label="simple table">
                    <TableBody>
                      <TableRow>
                        <TableCell>{t('message.common.total')}</TableCell>
                        <TableCell><NavLink href={`/equipment?searchSubway=${station?.category?.code}&searchStation=${station?.id}`}>{equipmentIotCount}</NavLink></TableCell>
                        <TableCell>{t('message.common.normal')}</TableCell>
                        <TableCell><NavLink className='font_green' href={`/equipment?searchSubway=${station?.category?.code}&searchStation=${station?.id}&searchStatus=O1_N`}>{normalCnt}</NavLink></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{t('message.common.interest')}</TableCell>
                        <TableCell><NavLink className='font_yellow' href={`/equipment?searchSubway=${station?.category?.code}&searchStation=${station?.id}&searchStatus=O2_I`}>{referCnt}</NavLink></TableCell>
                        <TableCell>{t('message.common.title.alarm')}</TableCell>
                        <TableCell><NavLink className='font_orange' href={`/equipment?searchSubway=${station?.category?.code}&searchStation=${station?.id}&searchStatus=O3_A`}>{alertCnt}</NavLink></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{t('message.common.subtitle.communication')}</TableCell>
                        <TableCell><NavLink className='font_gray' href={`/equipment?searchSubway=${station?.category?.code}&searchStation=${station?.id}&searchStatus=O4_C`}>{communiCnt}</NavLink></TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>                          
              </MainCard>
            </div> 

            {/* alert list */}
            <MainCard content={false} title="" className="graCard graCard1">
              <div style={{display: "flex", alignItems: "center"}}>
                <div style={{padding:"5px 10px"}}>
                  <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/dashboard.png`} alt='' /></span>&nbsp;
                  <Chip label={t('message.common.chip.dashboard.alert.list')} color="primary" />
                </div>
              </div>
              
              <div className="charts_box2">
                <AlertListCard equipmentAlerts={alertDatas?._embedded?.equipmentAlerts} target='sd' height="170px" />
              </div>
            </MainCard>

            {/* station category status */}
            <div className='cn_sub_equistatus' style={{marginBottom: "100px"}}>
              <div className='cn_sub_equistatus_tab'>
                {equipmentCategoryLevel1s && equipmentCategoryLevel1s.map((lvl1, l)=> {
                  const categoryData = equipmentCtgrResult && equipmentCtgrResult.find(ctgr => ctgr.code === lvl1.code);
                  // console.log(categoryData)
                  const alertCtrlCnt = categoryData?.data?.reduce((acc, cur)=>{
                    if (cur.name === 'O3_A') {
                        return acc + cur.count;
                    }
                    return acc
                  },0);
              
                  const referCtrlCnt = categoryData?.data?.reduce((acc, cur)=>{
                    if (cur.name === 'O2_I') {
                        return acc + cur.count;
                    }
                    return acc
                  },0);

                  let className = "";
                  if (alertCtrlCnt > 0 || referCtrlCnt > 0) {
                    className = "alert_sub";
                    if (alertCtrlCnt > 0) className += " orange_c";
                    else if (referCtrlCnt > 0) className += " yellow_c";
                  }
                  let selectClassName = '';
                  if (selectTab === lvl1.code) {
                    selectClassName = 'select_active';
                  }
                  return (
                    <div key={l} className={selectClassName} onClick={()=>{(selectTab===lvl1.code)?setSelectTab('ALL'):setSelectTab(lvl1.code)}}>
                      {className && <div className={className}><span>!</span></div>}
                      {/* {className && <Link to={`/dashboard/sub?subway=${station?.category?.code}&stationId=${station?.id}`} onClick={()=>{(selectTab===lvl1.code)?setSelectTab('ALL'):setSelectTab(lvl1.code)}}><div className={className}><span>!</span></div></Link>} */}
                      {/* <video src={`${process.env.PUBLIC_URL}/movie/${getEquipmentCtgrImageNameByCodeOfDashboardSubNantong(lvl1.code)}`}
                        className="detailEQMovie detailEQMovie_nantong_sub" preload='auto' loop
                        onClick={()=>{(selectTab===lvl1.code)?setSelectTab('ALL'):setSelectTab(lvl1.code)}}
                      ></video> */}
                      <img src={`${process.env.PUBLIC_URL}/img/small_motion/${getEquipmentCtgrImageNameByCodeOfDashboardSubNantong(lvl1.code)}`} alt={`Tab ${l}`} 
                        onClick={()=>{(selectTab===lvl1.code)?setSelectTab('ALL'):setSelectTab(lvl1.code)}} />
                    </div>
                  )
                })}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="g-3 graCard equip_tables nt_sub">
          {/* category equipment list */}
          <div className='equip_table'>
            <MainCard  content={false} title="">
            {equipmentCategoryLevel1s?.map((lvl1, l)=> {
              const equipmentsByLvl1 = (equipments?.find(equip => equip.code === lvl1.code)?.equipmentStatuses ?? []);
              // console.log(equipmentsByLvl1)
              return (
              <div key={l} style={{width:"100%", marginTop: "20px"}}>
                {(selectTab === 'ALL' || selectTab === lvl1.code) && 
              <div>
                <Chip label={`${lvl1.name} (${equipmentsByLvl1?.length})`} className='sub_status' style={{marginLeft:"10px"}} />
                <div className="small_motion_box">
                  {equipmentsByLvl1?.map((equip, e)=>{
                    let imgName = 'noimage.gif';
                    let imgClassName = 'bgwhite';
                    if (lvl1.code === 'EV') {
                      imgClassName = 'elevator_motion';
                      if (equip.latestEquipmentOp != null) {
                        imgName = getEquipmentCtgrImageNameByCodeOfDashboardSubNantong(lvl1.code, equip.latestEquipmentOp.opStatus, equip.latestEquipmentOp.opStatusDetail, equip.latestEquipmentOp.doorStatus, 'dashboard2');
                      } else {
                        imgName = getEquipmentCtgrImageNameByCodeOfDashboardSubNantong(lvl1.code, equip.latestEquipmentOp.opStatus, null, null, 'dashboard2');
                      }
                    } else if (lvl1.code === 'ES') {
                      imgClassName = 'elevator_motion';
                      if (equip.latestEquipmentOp != null) {
                        imgName = getEquipmentCtgrImageNameByCodeOfDashboardSubNantong(lvl1.code, equip.latestEquipmentOp.opStatus, equip.latestEquipmentOp.opStatusDetail, null, 'dashboard2');
                      } else {
                        imgName = getEquipmentCtgrImageNameByCodeOfDashboardSubNantong(lvl1.code, equip.latestEquipmentOp.opStatus, null, null, 'dashboard2');
                      }
                    } else {
                      // console.log(equip.latestEquipmentOp)
                      imgName = getEquipmentCtgrImageNameByCodeOfDashboardSubNantong(lvl1.code, equip.latestEquipmentOp.opStatus, null, null, 'dashboard2');
                    }

                    const isoGrade = equipmentISOStatus.find(iso=>iso.code === equip.lowestDiagnosisRank);

                    const alertEquipCnt = (equip.status === 'O3_A') ? 1 : 0;
                    const referEquipCnt = (equip.status === 'O2_I') ? 1 : 0;

                    let equipmentOpStatusClassName = '';
                    let equipmentOpStatusText = '';
                    if (equip.hasCommunicationIssue) {
                      equipmentOpStatusClassName = 'font_gray';
                      equipmentOpStatusText = COMM_STATUS_MAP[equip.hasCommunicationIssue];
                    } else {
                      equipmentOpStatusClassName = 'font_green';
                      equipmentOpStatusText = COMM_STATUS_MAP[false];
                    }

                    // let equipmentOpStatusText = '';
                    // if ((isViewCommError || role === ROLE.SA.code) && equip.hasCommunicationIssue) {
                    //     equipmentOpStatusClassName = 'font_gray';
                    //     equipmentOpStatusText = t('message.common.subtitle.communication.issue');
                    // } else {
                    //   if (equip.opStatus === 'S') {
                    //     equipmentOpStatusClassName = 'font_red';
                    //   } else if (equip.opStatus === 'O') {
                    //     equipmentOpStatusClassName = 'font_green';
                    //   }
                    //   equipmentOpStatusText = getEnumValueByEnumNameAndCode("EquipmentOpStatus",equip.opStatus)?.[0]?.title;
                    // }

                    let equipmentOpStatusClassName2 = '';
                    // console.log(equip.opStatus)
                    // if (equip.opStatus === 'S') {
                    if (equip.hasCommunicationIssue) {
                      equipmentOpStatusClassName2 = ' opacity03';
                    }
                    // console.log(equip.id, equip.latestEquipmentOp?.opStatus)
                    return (
                  <div key={e} className={`small_motion_group small_motion_group_nt${equipmentOpStatusClassName2}`}>
                    <p className="equipment_tit">* {equip.name}</p>
                    <div className="small_motion">
                      <div>
                        <div className={imgClassName}>
                          <NavLink href={`/equipment/${equip.id}`}>
                          <div className="alert_box">
                            {alertEquipCnt > 0 ? <div className="orange_c"><span>!</span></div> :
                            (
                            (referEquipCnt > 0 ? <div className="yellow_c"><span>!</span></div> : '')
                            )
                            }
                          </div>
                          {/* {lvl1.code === 'EV' && equip.latestEquipmentOp?.opMode?.currentFloor && <p className='floor'><span className='font_green'>{equip.latestEquipmentOp?.opMode?.integratedName || ''}</span><span>{equip.latestEquipmentOp?.currentFloor || '-'}</span><span className={`${equip.latestEquipmentOp?.opStatus === 'DOWN' ? 'downarrow':''} font_blue`}>{equip.latestEquipmentOp?.opStatus === 'UP' ?'▲':equip.latestEquipmentOp?.opStatus === 'DOWN' ? '▼':''}</span></p>}
                          {lvl1.code === 'ES' && equip.latestEquipmentOp?.opMode?.currentFloor && <p className='floor'><span className='font_green'>{equip.latestEquipmentOp?.opMode?.integratedName || ''}</span><span>-</span><span className={`${equip.latestEquipmentOp?.opStatus === 'DOWN' ? 'downarrow':''} font_blue`}>{equip.latestEquipmentOp?.opStatus === 'UP' ?'▲':equip.latestEquipmentOp?.opStatus === 'DOWN' ? '▼':''}</span></p>} */}
                          {/* {lvl1.code === 'EV' && equip.latestEquipmentOp?.currentFloor && <p className='floor'><span className='font_green'>{equip.latestEquipmentOp?.opMode?.integratedName || ''}</span><span>{equip.latestEquipmentOp?.opMode?.currentFloor || '-'}</span><span className={`${equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? 'downarrow':''} font_blue`}>{equip.latestEquipmentOp?.opStatusDetail === 'UP' ?'▲':equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? '▼':''}</span></p>}
                          {lvl1.code === 'ES' && equip.latestEquipmentOp?.opStatus === 'ACTIVE' && <p className='floor'><span className='font_green'>{equip.latestEquipmentOp?.opMode?.integratedName || ''}</span><span className={`${equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? 'downarrow':''} font_blue`}>{equip.latestEquipmentOp?.opStatusDetail === 'UP' ?'▲':equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? '▼':''}</span></p>} */}
                          {lvl1.code === 'EV' && equip.latestEquipmentOp?.currentFloor && <p className='floor'><span className='font_green'>{equip.latestEquipmentOp?.opMode?.integratedName || ''}</span><span>{equip.latestEquipmentOp?.currentFloor || '-'}</span><span className={`${equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? 'downarrow':equip.latestEquipmentOp?.opStatusDetail === 'IP' ? 'uparrow':''} font_blue`}>{equip.latestEquipmentOp?.opStatusDetail === 'UP' ?'▲':equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? '▼':''}</span></p>}
                          {lvl1.code === 'ES' && (equip.latestEquipmentOp?.opStatus === 'ACTIVE' ? <p className='floor'><span className='font_green'>{equip.latestEquipmentOp?.opMode?.integratedName || ''}</span><span className={`${equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? 'downarrow':equip.latestEquipmentOp?.opStatusDetail === 'UP' ? 'uparrow':''} font_blue`}>{equip.latestEquipmentOp?.opStatusDetail === 'UP' ?'▲':equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? '▼':''}</span></p>:<p className='floor'>■</p>)}
                          {/* {imgName && <video src={`${process.env.PUBLIC_URL}/movie/${imgName}`}
                            className="detailEQMovie detailEQMovie_nantong_sub" preload='auto' loop
                          ></video>} */}
                          {imgName && <img src={`${process.env.PUBLIC_URL}/img/small_motion/${imgName}`} alt='' />}
                          {(lvl1.code === 'EV' || lvl1.code === 'ES') && equip?.latestEquipmentOp?.controlPanelCode?.alertYn &&
                              <p className={`error${equip?.latestEquipmentOp?.controlPanelCode?.alertYn === 'N' ? ' font_green': ''}`}>
                              {equip?.latestEquipmentOp?.controlPanelCode?.alertYn === 'N' ? t('message.common.normal') : equip?.latestEquipmentOp?.controlPanelCode?.usrDfnCd}
                              {/* {equip?.latestEquipmentOp?.controlPanelCode?.alertYn === 'N' ? t('message.common.normal') : 'Error'} */}
                              </p>
                          }
                          </NavLink>
                        </div>

                        <TableContainer className="table_part">
                          <Table aria-label="simple table">
                            <TableBody>
                              <TableRow>
                                <TableCell>{t('message.common.subtitle.equipment.level3')}</TableCell>
                                <TableCell>{equip.equipmentTypeName}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>{t('message.common.subtitle.iso')}</TableCell>
                                <TableCell><div className={isoGrade?.color || ''}>{isoGrade?.title || '-'}</div></TableCell>
                              </TableRow>
                              {process.env.REACT_APP_TEMPERATURE_VIEW_YN === 'Y' &&
                              <TableRow>
                                <TableCell>{t('message.common.subtitle.equipment.temperature')}</TableCell>
                                <TableCell className={equip.maxTemperature && getTemperatureColor(equip.maxTemperature?.toFixed(0)) || ''}>{equip.maxTemperature?.toFixed(0) || '-'}</TableCell>
                                {/* <TableCell className={equip.minTemperature && getTemperatureColor(equip.minTemperature?.toFixed(0)) || ''}>{equip.minTemperature?.toFixed(0) || '-'}</TableCell> */}
                              </TableRow>
                              }
                              <TableRow>
                                <TableCell>{t('message.common.subtitle.defect')}</TableCell>
                                <TableCell><div className={getEquipmentStatusColorByStatus(equip.status).className}>{getEnumValueByEnumNameAndCode('EquipmentFaultStatus',equip.status)?.[0]?.title}</div></TableCell>
                              </TableRow>
                              {(isViewCommError || role === ROLE.SA.code) && 
                              <TableRow>
                                <TableCell>{t('message.common.subtitle.iot.status')}</TableCell>
                                <TableCell><div className={equipmentOpStatusClassName}>{equipmentOpStatusText}</div></TableCell>
                                {/* <TableCell className={equipmentOpStatusClassName}>{equipmentOpStatusText}</TableCell> */}
                              </TableRow>
                              } 
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </div>
                    </div>
                  </div>
                    )
                  })}
                </div>
              </div>
              }
              </div>
              )
            })}
            </MainCard>
          </div>
        </Row>
      </div> 
    )
}

export default DashboardNantongMoniteringSub;